"use client";

import * as React from "react";
import { ScrollEdgeArrows, useScrollEdges } from "./scroll-edges";
import { SIDEBAR_TABLET_ONLY, SIDEBAR_TABLET_SNAP_ROW } from "./layout";

/**
 * Tablet-band horizontal snap carousel for a sidebar widget's item list.
 *
 * Below `lg` a sidebar widget spans the full content width (see `SEGMENT_GRID`),
 * so its vertical list was reflowed into a multi-column grid. For a 4-item video
 * list that squeezed four 16:9 cards into ~170px each and still left the card's
 * CTA alone on a full-width row underneath. QA (`/columns/news`, tablet):
 * 「平板用這個組件明顯不是一個好嘅選擇 建議使用可以左右滑動嘅組件」.
 *
 * So on the tablet band only, the list becomes a swipeable row of 3.15-per-view
 * cells. Mobile (below the band) and the `lg` sidebar keep the plain vertical
 * column — everything here is a band-scoped override, nothing else.
 *
 * Every class string lives in `layout.ts`, because a `"use client"` module's
 * exports reach a server component as client-reference stubs: a constant
 * exported from here would stringify into `function(){throw Error(...)}` inside
 * a server component's `className`. Consumers mark their items with
 * `SIDEBAR_TABLET_SNAP_ITEM`, which pairs with this row's `gap-x`.
 *
 * Only the row is a client component; the cards are passed in as `children`
 * from a server component and stay server-rendered.
 */
export function TabletSnapCarousel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const edges = useScrollEdges(ref);

  return (
    <div className="relative w-full">
      <div
        ref={ref}
        className={`${SIDEBAR_TABLET_SNAP_ROW}${className ? ` ${className}` : ""}`}
      >
        {children}
      </div>
      {/* The scrollbar is hidden, so the row carries the shared ‹ / › edge
          affordances — one per direction that can actually move — exactly as
          the home strips do. They exist only in the band that scrolls. */}
      <div className={SIDEBAR_TABLET_ONLY}>
        <ScrollEdgeArrows scroller={ref} edges={edges} />
      </div>
    </div>
  );
}
